import React from 'react';

import { Select } from '@aline/common';
import { gql } from '@apollo/client';
import type { EditableNameVariant } from 'src/components';
import { AlineTooltip, EditableNameInput } from 'src/components';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { tooltipMsg } from 'src/helpers';
import { useCheckWorkflowAuthorization } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  input: {
    minWidth: 400,
  },
}));

void gql`
  query GetWorkflowName($workflowId: UUID!) {
    workflow(workflowId: $workflowId) {
      __typename
      id
      name
    }
  }
`;

interface WorkflowNameProps {
  workflowId: UUID | null;
  className?: string;
  fullWidth?: boolean;
  variant?: EditableNameVariant;
}

export function WorkflowNameInput({
  className,
  fullWidth,
  variant,
  workflowId,
}: WorkflowNameProps): JSX.Element | null {
  const { classes } = useStyles();
  const currentUserId = useCurrentUserId();
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflowId);
  const [updateWorkflow] = Apollo.useUpdateWorkflowMutation();
  const { data } = Apollo.useGetWorkflowNameQuery({
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
  });
  const disabledReason = !currentUserId
    ? 'Login to edit the workflow name'
    : !canEditWorkflow
      ? tooltipMsg.notWFEditor
      : '';

  async function updateName(name: string): Promise<void> {
    if (!workflowId) return;
    await updateWorkflow({
      variables: { input: { props: { name }, workflowId } },
    });
  }

  if (!workflowId) return null;

  return (
    <AlineTooltip arrow placement="bottom" title={disabledReason}>
      <div className={className}>
        <EditableNameInput
          dataCy={Select.workflow.detail.nameInput}
          disabled={Boolean(disabledReason)}
          fullWidth={fullWidth}
          inputBaseClassName={classes.input}
          name={data?.workflow?.name ?? ''}
          placeholder="Give your workflow a name..."
          updateName={updateName}
          variant={variant}
        />
      </div>
    </AlineTooltip>
  );
}
